import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Wand2, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { updateCardInDB } from "@/lib/db";
import { Card } from "@/types/linguadeck";
import {
  buildAiImagePrompt,
  getAiImageToastKey,
  requestAiImageUrl,
} from "@/lib/aiImageClient";
import { enrichCardWithAI } from "@/lib/flashcardEnrichment";
import { getStoredGeminiKeys } from "@/lib/apiKeys";
import LanguageSelector from "./LanguageSelector";
import { detectLanguageFromText } from "@/lib/detectLanguage";

interface EditSingleCardModalProps {
  card: Card;
  deckId: string;
  /** Deck title for AI image disambiguation (e.g. "Body Parts" → chest = human chest) */
  deckTitle?: string;
  onSave: (updates: {
    front: string;
    back: string;
    frontLang?: string | null;
    backLang?: string | null;
    frontAudioLocale?: string | null;
    backAudioLocale?: string | null;
    frontAudioUrl?: string | null;
    backAudioUrl?: string | null;
    imageUrl?: string | null;
    definition?: string | null;
    example?: string | null;
  }) => void;
  onClose: () => void;
}

export default function EditSingleCardModal({ card, deckId, deckTitle, onSave, onClose }: EditSingleCardModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [front, setFront] = useState(card.front ?? "");
  const [back, setBack] = useState(card.back ?? "");
  const [frontLang, setFrontLang] = useState(card.frontLang ?? "");
  const [backLang, setBackLang] = useState(card.backLang ?? "");
  const [definition, setDefinition] = useState(card.definition ?? "");
  const [example, setExample] = useState(card.example ?? "");
  const [imageUrl, setImageUrl] = useState<string | null>(card.imageUrl ?? null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [improvingWithAI, setImprovingWithAI] = useState(false);
  const regenerationHistoryRef = useRef<{ imageUrls: Set<string>; providerAssetIds: Set<string> }>({
    imageUrls: new Set<string>(),
    providerAssetIds: new Set<string>(),
  });
  const userHasChangedImageRef = useRef(false);

  useEffect(() => {
    userHasChangedImageRef.current = false;
    setFront(card.front ?? "");
    setBack(card.back ?? "");
    let nextFrontLang = (card.frontLang ?? "").toString().trim();
    let nextBackLang = (card.backLang ?? "").toString().trim();
    const backText = (card.back ?? "").trim();
    const frontText = (card.front ?? "").trim();
    if (backText && (nextBackLang === "ru" || nextBackLang.toLowerCase().startsWith("ru"))) {
      const detected = detectLanguageFromText(backText);
      if (detected === "uk") {
        nextBackLang = "uk";
        if (typeof console !== "undefined" && console.info) {
          console.info("[EditSingleCard] quizlet_card_language_mismatch_detected", { side: "back", corrected: "uk" });
        }
      }
    }
    if (frontText && (nextFrontLang === "ru" || nextFrontLang.toLowerCase().startsWith("ru"))) {
      const detected = detectLanguageFromText(frontText);
      if (detected === "uk") {
        nextFrontLang = "uk";
        if (typeof console !== "undefined" && console.info) {
          console.info("[EditSingleCard] quizlet_card_language_mismatch_detected", { side: "front", corrected: "uk" });
        }
      }
    }
    setFrontLang(nextFrontLang || "");
    setBackLang(nextBackLang || "");
    setDefinition(card.definition ?? "");
    setExample(card.example ?? "");
    setImageUrl(card.imageUrl ?? null);
    regenerationHistoryRef.current = {
      imageUrls: new Set(card.imageUrl ? [card.imageUrl] : []),
      providerAssetIds: new Set<string>(),
    };
  }, [card]);

  useEffect(() => {
    if (typeof console !== "undefined" && console.info) {
      console.info("[EditSingleCard] edit_single_card_image_block_rendered", {
        hasImage: !!(card.imageUrl ?? "").trim(),
      });
    }
  }, [card.imageUrl]);

  const rememberImageVariant = useCallback((nextImageUrl?: string | null, providerAssetId?: string) => {
    if (nextImageUrl) {
      regenerationHistoryRef.current.imageUrls.add(nextImageUrl);
    }
    if (providerAssetId) {
      regenerationHistoryRef.current.providerAssetIds.add(providerAssetId);
    }
  }, []);

  const handleSave = async () => {
    const trimmedFront = front.trim();
    const trimmedBack = back.trim();
    if (!trimmedFront && !trimmedBack) {
      toast.error(t("deckModal.enterWordFirst"));
      return;
    }
    if (!card.id) {
      toast.error(t("deckModal.uploadError"));
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[EditSingleCard] edit_card_save_skipped_no_id");
      }
      return;
    }

    const newFrontLang = frontLang.trim() || null;
    const newBackLang = backLang.trim() || null;
    const frontLangChanged = newFrontLang !== (card.frontLang ?? "").trim();
    const backLangChanged = newBackLang !== (card.backLang ?? "").trim();

    const imgVal = imageUrl == null ? null : (typeof imageUrl === "string" ? imageUrl : "").trim() || null;
    setSaving(true);
    if (typeof console !== "undefined" && console.info) {
      console.info("[EditSingleCard] edit_card_save_started", {
        frontLangChanged,
        backLangChanged,
        hasImageUrl: !!imgVal,
        cardId: card.id,
      });
    }
    try {
      const dbUpdates = {
        front: trimmedFront,
        back: trimmedBack,
        frontLang: newFrontLang,
        backLang: newBackLang,
        definition: definition.trim() || null,
        example: example.trim() || null,
        imageUrl: imgVal,
      };
      await updateCardInDB(card.id, dbUpdates);
      const savePayload = {
        front: trimmedFront,
        back: trimmedBack,
        frontLang: newFrontLang,
        backLang: newBackLang,
        definition: definition.trim() || null,
        example: example.trim() || null,
        imageUrl: imgVal,
        ...(frontLangChanged && { frontAudioLocale: null, frontAudioUrl: null }),
        ...(backLangChanged && { backAudioLocale: null, backAudioUrl: null }),
      };
      onSave(savePayload);
      if (typeof console !== "undefined" && console.info) {
        console.info("[EditSingleCard] edit_card_save_succeeded", { cardId: card.id, hasImageUrl: !!imgVal });
      }
      onClose();
    } catch (err) {
      if (typeof console !== "undefined" && console.error) {
        console.error("[EditSingleCard] edit_card_save_failed", err);
      }
      const isNoRow = (err as { code?: string })?.code === "CARD_UPDATE_NO_ROW";
      if (isNoRow && user?.id && deckId) {
        const sql = `UPDATE decks SET user_id = '${user.id}' WHERE id = '${deckId}' AND user_id != '${user.id}';`;
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[EditSingleCard] Deck may belong to another account. Run in Supabase SQL Editor:", sql);
        }
      }
      const msg = isNoRow ? t("deckModal.saveCardNoRow") : t("deckModal.uploadError");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;

    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 50);
      const path = `${user.id}/${Date.now()}_${safeName}`;

      const { error } = await supabase.storage
        .from("card_images")
        .upload(path, file, { upsert: false });

      if (error) throw error;

      const { data } = supabase.storage.from("card_images").getPublicUrl(path);
      userHasChangedImageRef.current = true;
      setImageUrl(data.publicUrl);
    } catch (err) {
      console.error("[EditSingleCard] Upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    userHasChangedImageRef.current = true;
    setImageUrl(null);
    if (typeof console !== "undefined" && console.info) {
      console.info("[EditSingleCard] edit_card_image_removed");
    }
  };

  const handleImproveWithAI = async () => {
    const word = front.trim();
    const translation = back.trim();
    if (!word || !translation) {
      toast.error(t("deckModal.enterWordFirst"));
      return;
    }
    // Keys from Settings/env; in production proxy mode empty is ok — server uses GEMINI_API_KEY
    const geminiKeys = getStoredGeminiKeys();

    setImprovingWithAI(true);
    try {
      const result = await enrichCardWithAI(
        word,
        translation,
        frontLang.trim() || "en",
        backLang.trim() || "en",
        geminiKeys,
        t("deckModal.improveWithAIError")
      );
      if (result.definition && !definition.trim()) setDefinition(result.definition);
      if (result.example && !example.trim()) setExample(result.example);
      if (result.back && result.back !== translation) setBack(result.back);
      toast.success(t("deckModal.improveWithAISuccess"));
    } catch (err) {
      console.error("[EditSingleCard] AI enrichment failed:", err);
      toast.error(t("deckModal.improveWithAIError"));
    } finally {
      setImprovingWithAI(false);
    }
  };

  const handleGenerateAIImage = async () => {
    const word = front.trim();
    if (!word) {
      toast.error(t("deckModal.enterWordFirst"));
      return;
    }
    if (!user) {
      toast.error(t("deckModal.signInToUpload"));
      return;
    }

    if (typeof console !== "undefined" && console.info) {
      console.info("[EditSingleCard] edit_card_image_generate_started");
    }
    setGeneratingAI(true);
    try {
      const generationMode = imageUrl ? "regenerate" : "initial";
      const promptWord = buildAiImagePrompt(word, back, card?.imagePrompt, {
        mode: generationMode,
        deckTitle: deckTitle || undefined,
      });
      const excludeImageUrls =
        generationMode === "regenerate"
          ? Array.from(
              new Set([
                ...Array.from(regenerationHistoryRef.current.imageUrls),
                ...(imageUrl ? [imageUrl] : []),
              ].filter(Boolean))
            )
          : undefined;
      const excludeProviderAssetIds =
        generationMode === "regenerate" && regenerationHistoryRef.current.providerAssetIds.size
          ? Array.from(regenerationHistoryRef.current.providerAssetIds)
          : undefined;
      const variationSeed =
        generationMode === "regenerate" ? Date.now() + Math.floor(Math.random() * 1_000) : undefined;
      const result = await requestAiImageUrl({
        prompt: promptWord,
        term: word,
        translationHint: back.trim() || undefined,
        sourceLanguage: frontLang.trim() || card.frontLang || undefined,
        targetLanguage: backLang.trim() || card.backLang || undefined,
        mode: generationMode,
        excludeImageUrls,
        excludeProviderAssetIds,
        variationSeed,
        userId: user.id,
      });
      if (result.kind === "no_image") {
        if (typeof console !== "undefined" && console.info) {
          console.info("[EditSingleCard] edit_card_image_generate_no_image");
        }
        toast.info(t("deckModal.aiImageNoImage"));
        return;
      }
      if (result.kind === "no_new_image") {
        if (typeof console !== "undefined" && console.info) {
          console.info("[EditSingleCard] edit_card_image_generate_no_new_image");
        }
        toast.info(t("deckModal.aiImageNoNewImage"));
        return;
      }
      const { imageUrl: generatedImageUrl, providerAssetId } = result;
      const wasReplacement = !!imageUrl;
      userHasChangedImageRef.current = true;
      setImageUrl(generatedImageUrl);
      rememberImageVariant(generatedImageUrl, providerAssetId);
      if (typeof console !== "undefined" && console.info) {
        console.info("[EditSingleCard] edit_card_image_generate_succeeded", {
          provider: result.provider,
          replaced: wasReplacement,
        });
        if (wasReplacement) {
          console.info("[EditSingleCard] edit_card_image_replaced");
        }
      }
    } catch (err) {
      if (typeof console !== "undefined" && console.error) {
        console.error("[EditSingleCard] edit_card_image_generate_error", err);
      }
      toast.error(t(getAiImageToastKey(err)));
    } finally {
      setGeneratingAI(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-md max-h-[min(90vh,640px)] flex flex-col rounded-2xl bg-[hsl(var(--card-bg))] border border-[hsl(var(--border))] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-none p-6 pb-0">
          <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-4">{t("edit_card")}</h3>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
              {t("deckModal.questionPlaceholder")}
            </label>
            <textarea
              value={front}
              onChange={(e) => setFront(e.target.value)}
              placeholder={t("deckModal.questionPlaceholder")}
              className="w-full min-h-[80px] px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] resize-y"
              rows={2}
            />
            <div className="flex justify-end mt-1">
              <LanguageSelector
                value={frontLang}
                onChange={(code) => setFrontLang(code ?? "")}
                placeholder={t("deckModal.selectLanguagePlaceholder")}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
              {t("deckModal.answerPlaceholder")}
            </label>
            <textarea
              value={back}
              onChange={(e) => setBack(e.target.value)}
              placeholder={t("deckModal.answerPlaceholder")}
              className="w-full min-h-[80px] px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] resize-y"
              rows={2}
            />
            <div className="flex justify-end mt-1">
              <LanguageSelector
                value={backLang}
                onChange={(code) => setBackLang(code ?? "")}
                placeholder={t("deckModal.selectLanguagePlaceholder")}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
              {t("flashcard.editCardImage")}
            </label>
            {(() => {
              const cardImg = (card as { imageUrl?: string | null; image_url?: string | null }).imageUrl ?? (card as { image_url?: string }).image_url;
              const userRemovedOrChanged = userHasChangedImageRef.current;
              const displayImageUrl = userRemovedOrChanged
                ? (imageUrl != null ? String(imageUrl).trim() || null : null)
                : (imageUrl ?? cardImg ?? "").trim() || null;
              const hasImage = !!displayImageUrl;
              return hasImage ? (
              <div className="flex flex-col gap-2">
                <div className="h-40 min-h-40 w-full shrink-0 rounded-lg overflow-hidden bg-[hsl(var(--muted))]/30 dark:bg-black/5 flex items-center justify-center">
                  <img src={String(displayImageUrl)} alt="" className="h-full w-full object-contain shrink-0 rounded-lg" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--bg3))]">
                    {uploading ? t("folder.loading") : t("flashcard.editCardChangeImage")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleGenerateAIImage}
                    disabled={generatingAI || !front.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 disabled:opacity-50"
                  >
                    <Wand2 size={14} />
                    {generatingAI ? t("folder.loading") : t("flashcard.generateImageWithAI")}
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="px-3 py-1.5 text-sm rounded-lg border border-rose-400/50 text-rose-500 hover:bg-rose-500/10"
                  >
                    {t("deckModal.clearImage")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-0.5">
                  {t("flashcard.editCardImageNoImageHint")}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleGenerateAIImage}
                    disabled={generatingAI || !front.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 disabled:opacity-50 font-medium"
                  >
                    <Wand2 size={16} />
                    {generatingAI ? t("folder.loading") : t("flashcard.generateImageWithAI")}
                  </button>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[hsl(var(--border))] hover:bg-[hsl(var(--bg3))] cursor-pointer">
                    {uploading ? t("folder.loading") : t("deckModal.addPhoto")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
            );
            })()}
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                {t("deckModal.definitionLabel")}
              </span>
              <button
                type="button"
                onClick={handleImproveWithAI}
                disabled={improvingWithAI || !front.trim() || !back.trim()}
                title={t("deckModal.improveWithAITooltip")}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 disabled:opacity-50"
              >
                <Sparkles size={12} />
                {improvingWithAI ? t("deckModal.improvingWithAI") : t("deckModal.improveWithAI")}
              </button>
            </div>
            <textarea
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              placeholder={t("deckModal.definitionPlaceholder")}
              className="w-full min-h-[56px] px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] text-sm resize-y"
              rows={1}
            />
            <textarea
              value={example}
              onChange={(e) => setExample(e.target.value)}
              placeholder={t("deckModal.examplePlaceholder")}
              className="w-full min-h-[56px] px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] text-sm resize-y"
              rows={1}
            />
          </div>
        </div>
        <div className="flex-none flex justify-end gap-2 p-6 pt-4 border-t border-[hsl(var(--border))]/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))]"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || (!front.trim() && !back.trim())}
            className="px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t("folder.loading") : t("deckModal.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
